import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import { completeNewPassword } from "../api-service/auth";
import { ROUTES } from "../common";
import messages from "../messages/ja.json";
import { AuthShell } from "../parts/auth-shell";
import { Icon, EyeIcon, EyeOffIcon } from "../parts/icon";
import { useAuth } from "../provider";

import "../styles/auth-flow.css";

interface ChangePasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const schema = yup.object({
  newPassword: yup
    .string()
    .required(messages.auth.passwordRequired)
    .test("password-policy", messages.validation.passwordPolicy, (value) => {
      if (!value) return false;
      if (value.length < 6) return false;
      if (!/[A-Z]/.test(value)) return false;
      if (!/[a-z]/.test(value)) return false;
      if (!/[0-9]/.test(value)) return false;
      return true;
    }),
  confirmPassword: yup
    .string()
    .required(messages.changePassword.confirmPasswordRequired)
    .oneOf([yup.ref("newPassword")], messages.validation.passwordMismatch),
});

export function ChangePasswordPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordForm>({ resolver: yupResolver(schema) });

  // Failed validation: the per-field messages stay under the inputs, and the toast
  // announces the failure at the top of the screen (Figma) — same as the login screen.
  const onInvalid = () => {
    toast.error(messages.changePassword.validationError, {
      icon: <Icon name="warning" size={27} />,
    });
  };

  const onSubmit = async (data: ChangePasswordForm) => {
    setSubmitting(true);
    try {
      const res = await completeNewPassword(data.newPassword);
      toast.success(messages.changePassword.success);

      // Route by the challenge the backend returns next. Face registration always comes before
      // secret-question enrollment: for FACE_REGISTRATION we go to /face-registration and carry
      // secretStatus from the flag, so once the face is registered the gate sends the user on to
      // /secret-questions when they still need to enroll.
      switch (res.challenge) {
        case "FACE_REGISTRATION":
        case "FIRST_TIME_MFA":
          auth.signin(
            res.user,
            "unregistered",
            res.secretRegistered ? "registered" : "unregistered",
            () => navigate(ROUTES.faceRegistration, { replace: true })
          );
          return;
        // Face already registered but secret questions not enrolled → enroll them now.
        case "SECRET_QUESTION_REQUIRED":
          auth.signin(res.user, "registered", "unregistered", () =>
            navigate(ROUTES.secretQuestions, { replace: true })
          );
          return;
        // Face already registered. Enroll secret questions first when the backend flags them as
        // not registered; otherwise onboarding is done → MFA verify.
        case "FACE_MFA":
        default:
          if (!res.secretRegistered) {
            auth.signin(res.user, "registered", "unregistered", () =>
              navigate(ROUTES.secretQuestions, { replace: true })
            );
            return;
          }
          auth.signin(res.user, "registered", "registered", () =>
            navigate(ROUTES.checkin, { replace: true })
          );
          return;
      }
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || messages.changePassword.error;
      toast.error(msg, { icon: <Icon name="warning" size={27} /> });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={messages.changePassword.title}
      className="change-password-shell"
      banner={
        <div className="auth-info-banner" role="status">
          <Icon name="info" size={18} className="auth-info-banner-icon" />
          <span>{messages.changePassword.firstTimeBanner}</span>
        </div>
      }
    >
      <form className="auth-form login-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <p className="login-card-copy">{messages.changePassword.description}</p>

        <label className="field">
          <span>{messages.changePassword.newPassword}</span>
          <div className="field-input-wrap">
            <input
              autoComplete="new-password"
              type={showNewPassword ? "text" : "password"}
              placeholder={messages.changePassword.newPasswordPlaceholder}
              disabled={submitting}
              aria-invalid={errors.newPassword ? "true" : undefined}
              {...register("newPassword")}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowNewPassword((v) => !v)}
              aria-label={showNewPassword ? messages.auth.hidePassword : messages.auth.showPassword}
              aria-pressed={showNewPassword}
            >
              {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {errors.newPassword ? <p className="field-error">{errors.newPassword.message}</p> : null}
        </label>

        <label className="field">
          <span>{messages.changePassword.confirmPassword}</span>
          <div className="field-input-wrap">
            <input
              autoComplete="new-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={messages.changePassword.confirmPasswordPlaceholder}
              disabled={submitting}
              aria-invalid={errors.confirmPassword ? "true" : undefined}
              {...register("confirmPassword")}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={
                showConfirmPassword ? messages.auth.hidePassword : messages.auth.showPassword
              }
              aria-pressed={showConfirmPassword}
            >
              {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          {errors.confirmPassword ? (
            <p className="field-error">{errors.confirmPassword.message}</p>
          ) : null}
        </label>

        <button className="primary-button button-full" type="submit" disabled={submitting}>
          {messages.changePassword.submit}
        </button>
      </form>
    </AuthShell>
  );
}
